/* biome-ignore-all lint/suspicious/noExplicitAny: legacy data structure */
import {
  UserOutlined,
  HistoryOutlined,
  ThunderboltOutlined,
  BookOutlined,
  EditOutlined,
  GlobalOutlined,
  KeyOutlined,
} from "@ant-design/icons"
import type { TablePaginationConfig, TableProps } from "antd"
import { Table, Space, Tag, Typography, Button, Tooltip, Badge } from "antd"

const { Text } = Typography
import type React from "react"
import { useTableScroll } from "@/hooks/useTableScroll"
import "@/styles/table.full.css"
import { formatDateTime } from "@/utils/dateUtils"
import { WEB_URL } from "@/configs/config"

interface UserTableProps {
  data: any[]
  loading: boolean
  selectedRowKeys: React.Key[]
  onSelectionChange: (selectedRowKeys: React.Key[], selectedRows: any[]) => void
  onOpenLogs?: (user: any) => void
  onOpenSrsLogs?: (user: any) => void
  onOpenSets?: (user: any) => void
  onEdit?: (user: any) => void
  onOpenTempPassword?: (user: any) => void
  pagination?: TableProps["pagination"]
}

const UserTable: React.FC<UserTableProps> = ({
  data,
  loading,
  selectedRowKeys,
  onSelectionChange,
  onOpenLogs,
  onOpenSrsLogs,
  onOpenSets,
  onEdit,
  onOpenTempPassword,
  pagination,
}) => {
  const { scrollConfig, tableWrapperRef } = useTableScroll()

  const columns: TableProps["columns"] = [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      fixed: "left",
      width: 60,
      align: "center",
      render: (value: string) => <span className="font-semibold">{value}</span>,
    },
    {
      title: "Thông tin",
      key: "user_info",
      width: 250,
      fixed: "left",
      render: (_: string, record: any) => (
        <div className="flex flex-col gap-0.5">
          <div className="flex flex-wrap items-center gap-1.5 font-medium text-gray-800">
            <Tooltip title={record.is_online ? "🟢 Đang trực tuyến (Online)" : "⚪ Ngoại tuyến (Offline)"}>
              <Badge status={record.is_online ? "success" : "default"} className="shrink-0 cursor-help" />
            </Tooltip>
            <UserOutlined className="text-blue-500!" />
            <span>{record.name || <span className="text-gray-400 italic">Chưa đặt tên</span>}</span>
            {record.is_online && (
              <span className="rounded-full border border-emerald-200 bg-emerald-50 px-1.5 py-0.2 font-medium text-[10px] text-emerald-600">
                Online
              </span>
            )}
            {record.is_test === 1 && (
              <Tag color="warning" className="m-0 px-1 py-0 font-semibold text-[10px]">
                🧪 Test
              </Tag>
            )}
          </div>
          <Text type="secondary" className="text-xs">
            {record.email}
          </Text>
        </div>
      ),
    },
    {
      title: "Tiến trình",
      key: "stats",
      width: 190,
      render: (_: any, record: any) => {
        const stat = record.stat
        if (!stat)
          return (
            <Text type="secondary" className="text-xs">
              —
            </Text>
          )

        return (
          <div className="flex flex-col gap-1 text-xs">
            <div className="flex items-center gap-2">
              <Tag color="purple" className="m-0 font-medium text-[11px]">
                {stat.level?.name || `Level ${stat.level_id || 1}`}
              </Tag>
              <span className="font-semibold text-amber-600">{stat.current_points?.toLocaleString() || 0} pts</span>
            </div>
            <div className="flex items-center gap-1 text-[11px] text-gray-500">
              <span>
                🔥 Chuỗi: <strong className="text-gray-700">{stat.current_streak || 0}</strong> ngày
              </span>
              <span className="text-gray-400">(Max: {stat.longest_streak || 0})</span>
            </div>
          </div>
        )
      },
    },
    {
      title: "Dữ liệu",
      key: "user_data",
      width: 130,
      align: "center",
      render: (_: any, record: any) => (
        <Space size={2}>
          <Tooltip title="Xem Nhật ký / Logs (Tìm kiếm & Thêm từ)">
            <Button
              type="text"
              size="small"
              icon={<HistoryOutlined className="text-base text-blue-500" />}
              onClick={(e) => {
                e.stopPropagation()
                if (onOpenLogs) onOpenLogs(record)
              }}
            />
          </Tooltip>
          <Tooltip title="Xem Nhật ký Học tập (SRS Logs)">
            <Button
              type="text"
              size="small"
              icon={<ThunderboltOutlined className="text-amber-500 text-base" />}
              onClick={(e) => {
                e.stopPropagation()
                if (onOpenSrsLogs) onOpenSrsLogs(record)
              }}
            />
          </Tooltip>
          <Tooltip title="Xem Bộ từ (Cá nhân & Jupi đã học)">
            <Button
              type="text"
              size="small"
              icon={<BookOutlined className="text-base text-emerald-600" />}
              onClick={(e) => {
                e.stopPropagation()
                if (onOpenSets) onOpenSets(record)
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
    {
      title: "Trạng thái",
      dataIndex: "is_active",
      key: "is_active",
      width: 120,
      align: "center",
      render: (value: number) => {
        if (value === 1) {
          return <Tag color="success">Kích hoạt</Tag>
        }
        return <Tag color="error">Khóa</Tag>
      },
    },
    {
      title: "Thời gian",
      key: "dates",
      render: (_: any, record: any) => (
        <Space direction="vertical" size={2}>
          <span className="text-xs">
            Lần cuối: {record.last_login_at ? formatDateTime(record.last_login_at, "dd/mm/yy hh:mm") : "Chưa đăng nhập"}
          </span>
          <span className="text-xs">
            Ngày tạo: {record.created_at ? formatDateTime(record.created_at, "dd/mm/yy hh:mm") : "_"}
          </span>
        </Space>
      ),
    },
    {
      title: "Thao tác",
      key: "action",
      width: 125,
      fixed: "right",
      align: "center",
      render: (_: any, record: any) => (
        <Space size={2}>
          <Tooltip title="Tạo mật khẩu tạm thời (Impersonation)">
            <Button
              type="text"
              size="small"
              icon={<KeyOutlined className="text-amber-500 text-base" />}
              onClick={(e) => {
                e.stopPropagation()
                if (onOpenTempPassword) onOpenTempPassword(record)
              }}
            />
          </Tooltip>
          <Tooltip title="Xem trang cá nhân trên Web">
            <Button
              type="text"
              size="small"
              icon={<GlobalOutlined className="text-base text-blue-600" />}
              onClick={(e) => {
                e.stopPropagation()
                window.open(`${WEB_URL}/thong-tin-ca-nhan/${record.id}`, "_blank", "noopener,noreferrer")
              }}
            />
          </Tooltip>
          <Tooltip title="Chỉnh sửa tài khoản">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined className="text-base text-orange-500" />}
              onClick={(e) => {
                e.stopPropagation()
                if (onEdit) onEdit(record)
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ]

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectionChange,
  }

  return (
    <div className="min-h-0 min-w-0 flex-1" ref={tableWrapperRef}>
      <Table
        bordered
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        rowSelection={rowSelection}
        pagination={pagination as TablePaginationConfig}
        scroll={{ x: "100%", y: scrollConfig.y }}
        size="small"
        classNames={{
          root: "full-height-table",
        }}
      />
    </div>
  )
}

UserTable.displayName = "UserTable"

export default UserTable
